import type { Vendor } from "@shared/types";
import { ANALYTICS_REGIONS, ANALYTICS_UNKNOWN } from "./events";
import type { AnalyticsDefaultProperties, AnalyticsRegion, AnalyticsTenureBand } from "./events";

/**
 * Who is acting, and on whose account.
 *
 * Assembled from the Salesforce-issued JWT the authorizer already decodes into
 * `VendorAttributes` — nothing here needs a request. `distinctId` binds the
 * session; the rest becomes root properties on every event.
 *
 * Every field except `tenureBand` is optional because the identity is built
 * before the vendor resolves and, for a Breville roaster admin, before a
 * roaster has been picked at all. Callers must handle the empty identity
 * rather than wait for a complete one: events fired during sign-in are real
 * events.
 */
export interface AnalyticsIdentity {
  /**
   * The Mixpanel `distinct_id` — the Salesforce `VendorContactId`.
   *
   * Stable across sessions and pseudonymous: it identifies a Contact record,
   * not a person, and carries no name, email or address. Never the email, and
   * never the `vendorKey`, which would collapse every user of a roaster into
   * one identity.
   */
  distinctId?: string;
  vendorKey?: string;
  region?: AnalyticsRegion;
  role?: string;
  tenureBand: AnalyticsTenureBand;
}

/** The identity before anything has resolved. */
export const EMPTY_ANALYTICS_IDENTITY: AnalyticsIdentity = { tenureBand: "unknown" };

const isAnalyticsRegion = (region: string): region is AnalyticsRegion => (ANALYTICS_REGIONS as readonly string[]).includes(region);

/**
 * Pins the token's `AccountRegion` to the `Region` union, or drops it.
 *
 * `Vendor.region` is a bare `string` all the way from the token, so an
 * unrecognised value reaching Mixpanel would sit beside the four real regions
 * and quietly split every regional report. Dropping it is the safer failure:
 * an absent property is visibly absent, a wrong one is not.
 */
export const resolveAnalyticsRegion = (region: string | undefined | null): AnalyticsRegion | undefined => {
  const trimmed = region?.trim();

  return trimmed && isAnalyticsRegion(trimmed) ? trimmed : undefined;
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Bands how long the roaster has been on the platform.
 *
 * Banded at the point of use so the raw date never reaches the pipeline, and
 * evaluated against `now` rather than stored, so the value is what was true
 * when the event fired.
 *
 * `"unknown"` covers an absent `createdAt` (vendor records predate the field),
 * an unparseable one, and a future-dated one — a clock skew or a bad backfill
 * must not present as a brand-new roaster and pollute the first-30-days
 * cohort that this banding exists to measure.
 */
export const resolveTenureBand = (createdAt: string | undefined | null, now: Date = new Date()): AnalyticsTenureBand => {
  if (!createdAt) {
    return "unknown";
  }

  const createdAtMs = Date.parse(createdAt);
  if (Number.isNaN(createdAtMs)) {
    return "unknown";
  }

  const days = Math.floor((now.getTime() - createdAtMs) / DAY_IN_MS);
  if (days < 0) {
    return "unknown";
  }
  if (days <= 30) {
    return "0-30d";
  }
  if (days <= 90) {
    return "31-90d";
  }
  if (days <= 365) {
    return "91-365d";
  }

  return "365d+";
};

/**
 * The vendor fields the identity is built from, plus the profile on the token.
 *
 * Declared as a `Partial<Pick<Vendor, …>>` rather than four loose strings so
 * renaming a field on the vendor schema fails this file's build instead of
 * quietly emptying a root property. All optional: the identity is built before
 * the vendor resolves.
 */
export interface BuildAnalyticsIdentityInput extends Partial<Pick<Vendor, "contactId" | "vendorKey" | "region" | "createdAt">> {
  /** The Salesforce profile name from the id token; becomes `role`. */
  profileName?: string | null;
  /**
   * A band already computed by the caller.
   *
   * `useAnalyticsIdentity` recomputes the band on every render so a long-lived
   * session cannot keep stamping a stale one, then memoises on the result. It
   * passes the value in rather than letting this function derive it again, so
   * the band the memo keyed on is exactly the band that ships.
   *
   * Omitted, the band is derived from `createdAt` as usual.
   */
  tenureBand?: AnalyticsTenureBand;
  /** Injectable for tests. */
  now?: Date;
}

/**
 * Builds the identity from the vendor in context and the profile on the token.
 *
 * Vendor fields must come from `useVendorContext()` — never
 * `useAuth().roaster`, which returns hardcoded mock data and would attribute
 * every event to a roaster that does not exist.
 *
 * Always returns an identity, never `undefined`. A half-resolved session still
 * fires events, and dropping them until everything arrives would lose exactly
 * the sign-in and onboarding steps that the funnel work is about.
 */
export const buildAnalyticsIdentity = ({
  contactId,
  vendorKey,
  region,
  createdAt,
  profileName,
  tenureBand,
  now,
}: BuildAnalyticsIdentityInput): AnalyticsIdentity => ({
  distinctId: contactId,
  vendorKey,
  region: resolveAnalyticsRegion(region),
  role: profileName?.trim() || undefined,
  tenureBand: tenureBand ?? resolveTenureBand(createdAt, now),
});

/**
 * The identity as root properties.
 *
 * `distinctId` is deliberately not among them: it reaches Freshpaint through
 * `identify()`, and repeating it as an event property would put a per-user
 * identifier on every payload for nothing.
 *
 * `region` and `role` fall back to `"unknown"` rather than being omitted, so
 * every event carries them and a report excludes one value instead of handling
 * a sometimes-missing key. This is the single place that fallback happens: the
 * identity itself keeps `undefined` for "not resolved", so `resolveAnalyticsRegion`
 * can stay strict about what counts as a real region.
 *
 * `vendorKey` is deliberately NOT defaulted. It is an account key — the
 * upgrade path to Group Analytics — and `"unknown"` there would be a
 * plausible-looking account that does not exist, which is worse than a gap.
 * An absent `vendorKey` means the vendor had not resolved yet.
 */
export const toRootIdentityProperties = (
  identity: AnalyticsIdentity
): Pick<AnalyticsDefaultProperties, "vendorKey" | "region" | "role" | "tenureBand"> => ({
  ...(identity.vendorKey ? { vendorKey: identity.vendorKey } : {}),
  region: identity.region ?? ANALYTICS_UNKNOWN,
  role: identity.role ?? ANALYTICS_UNKNOWN,
  tenureBand: identity.tenureBand,
});
